import React, { useState } from "react";
import type { Mode } from "./plateGame.ts";
import { Leaderboard } from "./plateLeaderboard.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

interface PlateLeaderboardProps {
    board: Leaderboard;
    /** The board to show: the mode being played, until a tab is pressed. */
    mode: Mode;
    onModeChange(mode: Mode): void;
    /** The row just saved, to highlight. */
    highlight: { mode: Mode; rank: number } | null;
    onClear(): void;
}

/**
 * The leaderboard beside the booth (ADR 0060 §5): the top ten for one
 * difficulty, a tab for the other, and a two-press Clear. Everything shown
 * here lives only in this browser (§6).
 */
export function PlateLeaderboard({ board, mode, onModeChange, highlight, onClear }: PlateLeaderboardProps) {
    const t = useTranslation();
    const [confirming, setConfirming] = useState(false);
    const entries = board[mode];
    const anything = board.easy.length > 0 || board.hard.length > 0;

    return (
        <aside className="plateBoard" aria-labelledby="plateBoardHeading">
            <h2 id="plateBoardHeading" className="plateBoardHeading">
                {t("flashCards.platesLeaderboard")}
            </h2>
            <div className="plateBoardTabs" role="group" aria-label={t("flashCards.platesLeaderboard")}>
                <button type="button" aria-pressed={mode === "easy"} onClick={() => onModeChange("easy")}>
                    {t("flashCards.platesEasy")}
                </button>
                <button type="button" aria-pressed={mode === "hard"} onClick={() => onModeChange("hard")}>
                    {t("flashCards.platesHard")}
                </button>
            </div>

            {entries.length === 0 ? (
                <p className="plateBoardEmpty">{t("flashCards.platesBoardEmpty")}</p>
            ) : (
                <table className="plateBoardTable">
                    <thead>
                        <tr>
                            <th scope="col">#</th>
                            <th scope="col">{t("flashCards.platesBoardName")}</th>
                            <th scope="col">{t("flashCards.platesBoardScore")}</th>
                            <th scope="col">{t("flashCards.platesBoardFacts")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((entry, i) => (
                            <tr
                                key={`${i}-${entry.name}-${entry.score}`}
                                className={highlight && highlight.mode === mode && highlight.rank === i ? "plateBoardRow--new" : undefined}
                            >
                                <td>{i + 1}</td>
                                <td className="plateBoardNameCell" title={entry.date}>
                                    {entry.name}
                                </td>
                                <td className="plateBoardScoreCell">{entry.score}</td>
                                <td className="plateBoardFactsCell">{entry.facts}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <p className="plateBoardNote">{t("flashCards.platesBoardNote")}</p>

            {anything &&
                (confirming ? (
                    <div className="plateBoardConfirm">
                        <span>{t("flashCards.platesClearConfirm")}</span>
                        <button
                            type="button"
                            className="plateBoardClearYes"
                            onClick={() => {
                                setConfirming(false);
                                onClear();
                            }}
                        >
                            {t("flashCards.platesClearYes")}
                        </button>
                        <button type="button" onClick={() => setConfirming(false)}>
                            {t("flashCards.platesClearNo")}
                        </button>
                    </div>
                ) : (
                    <button type="button" className="plateBoardClear" onClick={() => setConfirming(true)}>
                        {t("flashCards.platesClear")}
                    </button>
                ))}
        </aside>
    );
}
